import { useMemo, useRef, useState } from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	StyledButton,
	StyledContainer,
	StyledInput,
	StyledInputWrapper,
} from './CountPicker.styled';

const CountPicker = ({
	value,
	onChange,
	disabled,
	min = 1,
	max = 99,
	placeholder = 'шт.',
}: {
	value: number;
	onChange: (value: number) => void;
	disabled: boolean;
	min: number;
	max: number;
	placeholder?: string;
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const width = useMemo(() => {
		if (isFocused) {
			return '100%';
		}

		if (value < 10) {
			return 10;
		}

		return 18;
	}, [value, isFocused]);

	const handleButtonClick = (direction: 'left' | 'right') => {
		if (disabled) return;

		if (direction === 'left') {
			if (value > min) {
				onChange(value - 1);
			}
		} else {
			if (value < max) {
				onChange(value + 1);
			}
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (value < min) {
			onChange(min);
		} else if (value > max) {
			onChange(max);
		} else {
			onChange(value);
		}
	};

	const handleFocus = () => {
		setIsFocused(true);
		inputRef.current?.focus();
	};

	return (
		<StyledContainer>
			<StyledButton
				$direction="left"
				$disabled={disabled}
				onClick={() => {
					handleButtonClick('left');
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				onMouseDown={(e) => e.preventDefault()}
			>
				<img src={arrowLeft} />
			</StyledButton>
			<StyledInputWrapper $disabled={disabled} onClick={handleFocus}>
				<StyledInput
					ref={inputRef}
					type="number"
					size={2}
					value={value}
					onChange={handleChange}
					disabled={disabled}
					min={min}
					max={max}
					style={{ width }}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					readOnly={disabled}
				/>
				{!isFocused && <Text>{placeholder}</Text>}
			</StyledInputWrapper>
			<StyledButton
				$direction="right"
				$disabled={disabled}
				onClick={() => {
					handleButtonClick('right');
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
				onMouseDown={(e) => e.preventDefault()}
			>
				<img src={arrowLeft} style={{ transform: 'rotate(180deg)' }} />
			</StyledButton>
		</StyledContainer>
	);
};

export { CountPicker };
